// The weekly leaderboard card (iOS TeamSnapshotSection, "Team this week"): the top five by visits this week, with
// rank (the leader's in amber), avatar, name and role, "12 visits" and a YOU chip, linking to /leaderboard.

import { Medal } from 'lucide-react';
import { Link } from 'react-router';
import { fullName } from '@/domain/util';
import { Avatar, Chip, EmptyState, SectionCard, cn } from '@/ui';
import { roleTitle, visitsText, type RankedUser } from './dashboardModel';

export function LeaderboardCard({ entries, className }: { entries: readonly RankedUser[]; className?: string }) {
  return (
    <SectionCard
      title="Team this week"
      actionLabel="Leaderboards"
      actionHref="/leaderboard"
      padding="none"
      bodyClassName="overflow-hidden"
      className={className}
    >
      {entries.length === 0 ? (
        <EmptyState
          compact
          icon={<Medal />}
          color="#F5B400"
          title="No team activity yet"
          message="Visits logged by your reps this week will rank here."
        />
      ) : (
        <ol aria-label="Top reps by visits this week" className="divide-y divide-line">
          {entries.map((e) => {
            const name = fullName(e.user) || 'Unnamed';
            return (
              <li key={e.user.id}>
                <Link
                  to={`/team/${e.user.id}`}
                  aria-label={`Number ${e.rank}: ${name}, ${visitsText(e.value)}${e.isMe ? ' (you)' : ''}`}
                  className="flex min-w-0 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-surface-2"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'w-4 shrink-0 text-center font-rounded text-xs font-bold tabular-nums',
                      e.rank === 1 ? 'text-amber' : 'text-fg-muted',
                    )}
                  >
                    {e.rank}
                  </span>
                  <Avatar name={name} colorHex={e.user.colorHex} size={30} label={null} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-semibold text-fg">{name}</span>
                    <span className="truncate text-[11px] text-fg-muted">{roleTitle(e.user)}</span>
                  </span>
                  <span className="shrink-0 text-sm font-bold text-fg tabular-nums">{visitsText(e.value)}</span>
                  {e.isMe && (
                    <Chip size="xs" color="#F96A3C">
                      You
                    </Chip>
                  )}
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}
